import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton,
  Collapse,
  Autocomplete,
  Slider,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Send as SendIcon,
  History as HistoryIcon,
  Devices as DevicesIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { adminApi } from '../services/adminApi';

const NOTIFICATION_TYPES = [
  { value: 'admin_test', label: 'Admin Test' },
  { value: 'admin_announcement', label: 'Admin Announcement' },
  { value: 'dietitian_message', label: 'Dietitian Message' },
  { value: 'inquiry_status_change', label: 'Inquiry Status Change' },
  { value: 'challenge_assigned', label: 'Challenge Assigned' },
  { value: 'family_meal_logged', label: 'Family Meal Logged' },
  { value: 'weekly_insight_ready', label: 'Weekly Insight' },
  { value: 'streak_at_risk', label: 'Streak at Risk' },
  { value: 'gentle_reengagement', label: 'Re-engagement Nudge' },
  { value: 'goal_milestone', label: 'Goal Milestone' },
  { value: 'challenge_ending', label: 'Challenge Ending Soon' },
  { value: 'patient_request', label: 'Patient Request (Dietitian)' },
  { value: 'patient_milestone', label: 'Patient Milestone (Dietitian)' },
  { value: 'patient_inactive', label: 'Patient Inactive (Dietitian)' },
];

const TYPE_COLORS: Record<string, string> = {
  streak_at_risk: '#FF6B35',
  challenge_ending: '#E91E63',
  gentle_reengagement: '#9C27B0',
  weekly_insight_ready: '#2196F3',
  goal_milestone: '#4CAF50',
  dietitian_message: '#FF8C00',
  inquiry_status_change: '#FF8C00',
  challenge_assigned: '#FF8C00',
  family_meal_logged: '#00BCD4',
  admin_test: '#607D8B',
  admin_announcement: '#8B5CF6',
  patient_request: '#6366F1',
  patient_milestone: '#0EA5E9',
  patient_inactive: '#F59E0B',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function profileName(profile: any): string {
  if (!profile) return 'Unknown';
  const parts = [profile.first_name, profile.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : profile.username || 'Unknown';
}

export function PushNotificationsTab() {
  // Section collapse state
  const [showStats, setShowStats] = useState(true);
  const [showSend, setShowSend] = useState(true);
  const [showLogs, setShowLogs] = useState(true);
  const [showTokens, setShowTokens] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  // Stats
  const [stats, setStats] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  // Send push
  const [sendTitle, setSendTitle] = useState('');
  const [sendBody, setSendBody] = useState('');
  const [sendType, setSendType] = useState('admin_test');
  const [sendUserId, setSendUserId] = useState('');
  const [skipRateLimit, setSkipRateLimit] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<any>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  // Logs
  const [logs, setLogs] = useState<any[]>([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const [logsPerPage, setLogsPerPage] = useState(25);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [logTypeFilter, setLogTypeFilter] = useState('');
  const [logChannelFilter, setLogChannelFilter] = useState('');

  // Tokens
  const [tokens, setTokens] = useState<any[]>([]);
  const [tokensTotal, setTokensTotal] = useState(0);
  const [tokensPage, setTokensPage] = useState(0);
  const [tokensPerPage, setTokensPerPage] = useState(25);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [tokenSearch, setTokenSearch] = useState('');

  // Scheduler config
  const [config, setConfig] = useState<any>(null);
  const [defaults, setDefaults] = useState<any>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configDirty, setConfigDirty] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);

  // Token user picker for send
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [tokenRoleFilter, setTokenRoleFilter] = useState<string>('');

  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const { stats: s } = await adminApi.getNotificationStats();
      setStats(s);
    } catch { /* non-fatal */ }
    setLoadingStats(false);
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const { logs: l, total } = await adminApi.getNotificationLogs({
        limit: logsPerPage,
        offset: logsPage * logsPerPage,
        ...(logTypeFilter ? { type: logTypeFilter } : {}),
        ...(logChannelFilter ? { channel: logChannelFilter } : {}),
      });
      setLogs(l);
      setLogsTotal(total);
    } catch { /* non-fatal */ }
    setLoadingLogs(false);
  }, [logsPage, logsPerPage, logTypeFilter, logChannelFilter]);

  const fetchTokens = useCallback(async () => {
    setLoadingTokens(true);
    try {
      const { tokens: t, total } = await adminApi.getNotificationTokens({
        limit: tokensPerPage,
        offset: tokensPage * tokensPerPage,
        ...(tokenSearch ? { search: tokenSearch } : {}),
      });
      setTokens(t);
      setTokensTotal(total);
      const allOpts = t.filter((tk: any) => tk.profiles).map((tk: any) => ({
        user_id: tk.user_id,
        role: tk.role || 'unknown',
        label: `${profileName(tk.profiles)} (${tk.role === 'dietitian' ? 'Dietitian' : tk.role === 'patient' ? 'Patient' : 'Unknown'}) — ${tk.profiles?.email || tk.user_id.slice(0, 8)}`,
      }));
      setUserOptions(allOpts);
    } catch { /* non-fatal */ }
    setLoadingTokens(false);
  }, [tokensPage, tokensPerPage, tokenSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchConfig = useCallback(async () => {
    setLoadingConfig(true);
    try {
      const { config: c, defaults: d } = await adminApi.getSchedulerConfig();
      setConfig(c);
      setDefaults(d);
      setConfigDirty(false);
    } catch { /* non-fatal */ }
    setLoadingConfig(false);
  }, []);

  useEffect(() => { fetchStats(); fetchLogs(); }, [fetchStats, fetchLogs]);

  useEffect(() => {
    if (showTokens) fetchTokens();
  }, [showTokens, fetchTokens]);

  useEffect(() => {
    if (showConfig && !config) fetchConfig();
  }, [showConfig, config, fetchConfig]);

  const handleSend = async () => {
    if (!sendTitle.trim() || !sendBody.trim()) return;
    const target = sendUserId.trim();
    if (!target) {
      setSendError('Please enter a user ID or select a user');
      return;
    }

    const confirmed = window.confirm(
      `Send push notification to user ${target}?\n\nTitle: ${sendTitle}\nBody: ${sendBody}\nType: ${sendType}`,
    );
    if (!confirmed) return;

    setSending(true);
    setSendResult(null);
    setSendError(null);
    try {
      const result = await adminApi.sendTestPush({
        userId: target,
        type: sendType,
        title: sendTitle,
        body: sendBody,
        skipRateLimit,
      });
      setSendResult(result);
      fetchLogs();
      fetchStats();
    } catch (err: any) {
      setSendError(err.response?.data?.error || err.message || 'Failed to send');
    }
    setSending(false);
  };

  const handleSaveConfig = async () => {
    if (!config) return;
    setSavingConfig(true);
    setConfigSaved(false);
    try {
      const { config: updated } = await adminApi.updateSchedulerConfig(config);
      setConfig(updated);
      setConfigDirty(false);
      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 3000);
    } catch { /* non-fatal */ }
    setSavingConfig(false);
  };

  const updateConfigField = (field: string, value: any) => {
    setConfig((prev: any) => ({ ...prev, [field]: value }));
    setConfigDirty(true);
  };

  const updateCooldown = (type: string, hours: number) => {
    setConfig((prev: any) => ({
      ...prev,
      cooldowns_hours: { ...prev.cooldowns_hours, [type]: hours },
    }));
    setConfigDirty(true);
  };

  const toggleEnabledType = (type: string) => {
    setConfig((prev: any) => {
      const current = prev.enabled_types || [];
      const updated = current.includes(type)
        ? current.filter((t: string) => t !== type)
        : [...current, type];
      return { ...prev, enabled_types: updated };
    });
    setConfigDirty(true);
  };

  // ──────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <NotificationsIcon sx={{ color: '#FF8C00', fontSize: 28 }} />
          <Typography variant="h5" fontWeight={700}>Push Notifications</Typography>
        </Stack>
        <Button
          size="small" variant="text" startIcon={<RefreshIcon />}
          onClick={() => { fetchStats(); fetchLogs(); }}
          sx={{ textTransform: 'none', color: '#FF8C00' }}
        >
          Refresh All
        </Button>
      </Stack>

      {/* ── STATS ── */}
      <SectionHeader
        icon={<TrendingUpIcon sx={{ color: '#4CAF50' }} />}
        title="Overview"
        open={showStats}
        onToggle={() => setShowStats(!showStats)}
      />
      <Collapse in={showStats}>
        {loadingStats ? (
          <Box py={3} textAlign="center"><CircularProgress size={24} /></Box>
        ) : stats ? (
          <Stack direction="row" spacing={2} mb={3} flexWrap="wrap" useFlexGap>
            <StatCard label="Registered Devices" value={stats.registered_devices} color="#2196F3" />
            <StatCard label="Last 24h" value={stats.pushes_last_24h} color="#4CAF50" />
            <StatCard label="Last 7 days" value={stats.pushes_last_7d} color="#FF8C00" />
            <StatCard label="Last 30 days" value={stats.pushes_last_30d} color="#9C27B0" />
            {Object.entries(stats.by_type_30d || {}).map(([type, count]) => (
              <StatCard
                key={type}
                label={NOTIFICATION_TYPES.find(t => t.value === type)?.label || type}
                value={count as number}
                color={TYPE_COLORS[type] || '#607D8B'}
                small
              />
            ))}
          </Stack>
        ) : null}
      </Collapse>

      {/* ── SEND TEST PUSH ── */}
      <SectionHeader
        icon={<SendIcon sx={{ color: '#FF8C00' }} />}
        title="Send Push Notification"
        open={showSend}
        onToggle={() => setShowSend(!showSend)}
      />
      <Collapse in={showSend}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
          {sendResult && (
            <Alert severity={sendResult.sent > 0 ? 'success' : 'warning'} sx={{ mb: 2 }} onClose={() => setSendResult(null)}>
              <strong>Sent to {sendResult.sent} device(s), {sendResult.skipped} skipped.</strong>
              {sendResult.skipReasons?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {sendResult.skipReasons.map((r: string, i: number) => (
                    <Typography key={i} variant="body2" sx={{ mt: 0.5 }}>&bull; {r}</Typography>
                  ))}
                </Box>
              )}
              {sendResult.errors?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {sendResult.errors.map((e: string, i: number) => (
                    <Typography key={i} variant="body2" color="error" sx={{ mt: 0.5 }}>&bull; {e}</Typography>
                  ))}
                </Box>
              )}
            </Alert>
          )}
          {sendError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSendError(null)}>{sendError}</Alert>
          )}

          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <Autocomplete
                freeSolo
                options={userOptions}
                getOptionLabel={(opt: any) => typeof opt === 'string' ? opt : opt.label}
                onChange={(_, val) => {
                  if (val && typeof val !== 'string') setSendUserId(val.user_id);
                }}
                onInputChange={(_, val) => setSendUserId(val)}
                inputValue={sendUserId}
                sx={{ flex: 2 }}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="User ID" placeholder="Paste user ID or select..." />
                )}
              />
              <FormControl size="small" sx={{ flex: 1, minWidth: 180 }}>
                <InputLabel>Type</InputLabel>
                <Select value={sendType} label="Type" onChange={e => setSendType(e.target.value)}>
                  {NOTIFICATION_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>

            <TextField size="small" label="Title" value={sendTitle} onChange={e => setSendTitle(e.target.value)} fullWidth />
            <TextField size="small" label="Body" value={sendBody} onChange={e => setSendBody(e.target.value)} fullWidth multiline minRows={2} />

            <Stack direction="row" alignItems="center" spacing={2}>
              <FormControlLabel
                control={<Switch checked={skipRateLimit} onChange={e => setSkipRateLimit(e.target.checked)} size="small" />}
                label={<Typography variant="body2">Skip rate limit</Typography>}
              />
              <Button
                variant="contained"
                startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                onClick={handleSend}
                disabled={sending || !sendTitle.trim() || !sendBody.trim() || !sendUserId.trim()}
                sx={{
                  borderRadius: '20px', textTransform: 'none', fontWeight: 700, px: 3,
                  bgcolor: '#FF8C00', '&:hover': { bgcolor: '#E07A00' },
                }}
              >
                {sending ? 'Sending...' : 'Send Push'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Collapse>

      {/* ── NOTIFICATION LOG ── */}
      <SectionHeader
        icon={<HistoryIcon sx={{ color: '#2196F3' }} />}
        title="Notification Log"
        open={showLogs}
        onToggle={() => setShowLogs(!showLogs)}
        badge={logsTotal}
      />
      <Collapse in={showLogs}>
        <Paper variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
          <Stack direction="row" spacing={1.5} p={1.5} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Type</InputLabel>
              <Select value={logTypeFilter} label="Type" onChange={e => { setLogTypeFilter(e.target.value); setLogsPage(0); }}>
                <MenuItem value="">All Types</MenuItem>
                {NOTIFICATION_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Channel</InputLabel>
              <Select value={logChannelFilter} label="Channel" onChange={e => { setLogChannelFilter(e.target.value); setLogsPage(0); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="push">Push</MenuItem>
                <MenuItem value="in_app">In-App</MenuItem>
              </Select>
            </FormControl>
            <Box flex={1} />
            <IconButton size="small" onClick={fetchLogs}><RefreshIcon fontSize="small" /></IconButton>
          </Stack>

          {loadingLogs ? (
            <Box py={4} textAlign="center"><CircularProgress size={24} /></Box>
          ) : logs.length === 0 ? (
            <Typography variant="body2" color="text.secondary" p={3} textAlign="center">
              No notifications sent yet.
            </Typography>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Body</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Channel</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Sent</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log: any) => (
                    <TableRow key={log.id} hover>
                      <TableCell sx={{ fontSize: '0.8rem', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={log.user_id}>
                          <span>{profileName(log.profiles)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={NOTIFICATION_TYPES.find(t => t.value === log.notification_type)?.label || log.notification_type}
                          size="small"
                          sx={{
                            bgcolor: `${TYPE_COLORS[log.notification_type] || '#607D8B'}18`,
                            color: TYPE_COLORS[log.notification_type] || '#607D8B',
                            fontWeight: 600, fontSize: '0.7rem', height: 22,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.title}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.body}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.channel}
                          size="small"
                          sx={{
                            bgcolor: log.channel === 'push' ? '#E3F2FD' : '#FFF3E0',
                            color: log.channel === 'push' ? '#1565C0' : '#E65100',
                            fontSize: '0.7rem', height: 20, fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        <Tooltip title={new Date(log.sent_at).toLocaleString()}>
                          <span>{timeAgo(log.sent_at)}</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={logsTotal}
                page={logsPage}
                onPageChange={(_, p) => setLogsPage(p)}
                rowsPerPage={logsPerPage}
                onRowsPerPageChange={e => { setLogsPerPage(parseInt(e.target.value)); setLogsPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </Paper>
      </Collapse>

      {/* ── PUSH TOKENS ── */}
      <SectionHeader
        icon={<DevicesIcon sx={{ color: '#00BCD4' }} />}
        title="Registered Devices"
        open={showTokens}
        onToggle={() => setShowTokens(!showTokens)}
        badge={tokensTotal || stats?.registered_devices}
      />
      <Collapse in={showTokens}>
        <Paper variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
          <Stack direction="row" spacing={1.5} p={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              size="small" placeholder="Search by name or email..."
              value={tokenSearch}
              onChange={e => { setTokenSearch(e.target.value); setTokensPage(0); }}
              sx={{ minWidth: 240 }}
            />
            <Chip
              label="All" size="small" variant={tokenRoleFilter === '' ? 'filled' : 'outlined'}
              onClick={() => setTokenRoleFilter('')}
              sx={{ fontWeight: 600, ...(tokenRoleFilter === '' ? { bgcolor: '#607D8B', color: '#fff' } : {}) }}
            />
            <Chip
              label="Dietitians" size="small" variant={tokenRoleFilter === 'dietitian' ? 'filled' : 'outlined'}
              onClick={() => setTokenRoleFilter(tokenRoleFilter === 'dietitian' ? '' : 'dietitian')}
              sx={{ fontWeight: 600, ...(tokenRoleFilter === 'dietitian' ? { bgcolor: '#6366F1', color: '#fff' } : {}) }}
            />
            <Chip
              label="Patients" size="small" variant={tokenRoleFilter === 'patient' ? 'filled' : 'outlined'}
              onClick={() => setTokenRoleFilter(tokenRoleFilter === 'patient' ? '' : 'patient')}
              sx={{ fontWeight: 600, ...(tokenRoleFilter === 'patient' ? { bgcolor: '#059669', color: '#fff' } : {}) }}
            />
            <Box flex={1} />
            <IconButton size="small" onClick={fetchTokens}><RefreshIcon fontSize="small" /></IconButton>
          </Stack>

          {loadingTokens ? (
            <Box py={4} textAlign="center"><CircularProgress size={24} /></Box>
          ) : tokens.length === 0 ? (
            <Typography variant="body2" color="text.secondary" p={3} textAlign="center">
              No push tokens registered.
            </Typography>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Platform</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Token</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Last Active</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tokens
                    .filter((tok: any) => !tokenRoleFilter || tok.role === tokenRoleFilter)
                    .map((tok: any) => (
                    <TableRow key={tok.id || tok.token} hover>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{profileName(tok.profiles)}</TableCell>
                      <TableCell>
                        <Chip
                          label={tok.role === 'dietitian' ? 'Dietitian' : tok.role === 'patient' ? 'Patient' : '—'}
                          size="small"
                          sx={{
                            fontSize: '0.7rem', height: 20, fontWeight: 600,
                            bgcolor: tok.role === 'dietitian' ? '#EEF2FF' : tok.role === 'patient' ? '#ECFDF5' : '#F5F5F5',
                            color: tok.role === 'dietitian' ? '#6366F1' : tok.role === 'patient' ? '#059669' : '#999',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{tok.profiles?.email || '—'}</TableCell>
                      <TableCell>
                        <Chip label={tok.platform || 'unknown'} size="small"
                          sx={{ fontSize: '0.7rem', height: 20, fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={tok.token}><span>{tok.token}</span></Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {tok.last_active_at ? timeAgo(tok.last_active_at) : '—'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Send test push to this user">
                          <IconButton size="small" onClick={() => {
                            setSendUserId(tok.user_id);
                            setSendTitle('Test Notification');
                            setSendBody('This is a test push from the admin dashboard.');
                            setShowSend(true);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}>
                            <SendIcon fontSize="small" sx={{ color: '#FF8C00' }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={tokensTotal}
                page={tokensPage}
                onPageChange={(_, p) => setTokensPage(p)}
                rowsPerPage={tokensPerPage}
                onRowsPerPageChange={e => { setTokensPerPage(parseInt(e.target.value)); setTokensPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </Paper>
      </Collapse>

      {/* ── SCHEDULER CONFIG ── */}
      <SectionHeader
        icon={<SettingsIcon sx={{ color: '#9C27B0' }} />}
        title="Scheduled Notification Settings"
        open={showConfig}
        onToggle={() => setShowConfig(!showConfig)}
      />
      <Collapse in={showConfig}>
        {loadingConfig ? (
          <Box py={4} textAlign="center"><CircularProgress size={24} /></Box>
        ) : config ? (
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
            {configSaved && <Alert severity="success" sx={{ mb: 2 }}>Configuration saved successfully.</Alert>}

            <Stack spacing={3}>
              {/* Global limits */}
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1}>
                  Global Limits
                </Typography>
                <Stack direction="row" spacing={3} alignItems="center">
                  <Box sx={{ flex: 1, maxWidth: 280 }}>
                    <Typography variant="body2" mb={0.5}>Max push notifications per user per day</Typography>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Slider
                        value={config.max_push_per_day}
                        onChange={(_, v) => updateConfigField('max_push_per_day', v)}
                        min={0} max={5} step={1}
                        marks={[{ value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 5, label: '5' }]}
                        sx={{ color: '#FF8C00' }}
                      />
                      <Chip label={config.max_push_per_day} sx={{ fontWeight: 700, minWidth: 36 }} />
                    </Stack>
                  </Box>
                  <Box sx={{ flex: 1, maxWidth: 280 }}>
                    <Typography variant="body2" mb={0.5}>Quiet window after app open (hours)</Typography>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Slider
                        value={config.quiet_window_hours}
                        onChange={(_, v) => updateConfigField('quiet_window_hours', v)}
                        min={1} max={12} step={1}
                        marks={[{ value: 1, label: '1h' }, { value: 6, label: '6h' }, { value: 12, label: '12h' }]}
                        sx={{ color: '#FF8C00' }}
                      />
                      <Chip label={`${config.quiet_window_hours}h`} sx={{ fontWeight: 700, minWidth: 36 }} />
                    </Stack>
                  </Box>
                </Stack>
              </Box>

              <Divider />

              {/* Scheduled notification types */}
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1.5}>
                  Scheduled Notification Types
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                  Toggle which types the scheduler can send, and set the cooldown (minimum hours between same-type notifications per user).
                </Typography>

                <Stack spacing={1.5}>
                  {[
                    { type: 'streak_at_risk', label: 'Streak at Risk', desc: '3+ day streak, no meal today, sent in evening' },
                    { type: 'challenge_ending', label: 'Challenge Ending Soon', desc: 'Active challenge with < 24h remaining' },
                    { type: 'gentle_reengagement', label: 'Re-engagement Nudge', desc: 'No app activity in 3+ days (escalating backoff: Day 3, 7, 14)' },
                    { type: 'weekly_insight_ready', label: 'Weekly Insight', desc: 'Sunday evening for users with 3+ days of intake data that week' },
                    { type: 'goal_milestone', label: 'Goal Milestone', desc: 'Hit a nutrition goal X days in a row (every 5-day milestone)' },
                  ].map(({ type, label, desc }) => {
                    const enabled = (config.enabled_types || []).includes(type);
                    const cooldown = config.cooldowns_hours?.[type] ?? defaults?.cooldowns_hours?.[type] ?? 72;
                    return (
                      <Paper key={type} variant="outlined" sx={{ p: 1.5, borderRadius: 2, opacity: enabled ? 1 : 0.5 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Switch
                            checked={enabled}
                            onChange={() => toggleEnabledType(type)}
                            size="small"
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: TYPE_COLORS[type] } }}
                          />
                          <Box flex={1}>
                            <Typography variant="body2" fontWeight={700}>
                              <Chip
                                label={label} size="small"
                                sx={{
                                  bgcolor: `${TYPE_COLORS[type]}18`, color: TYPE_COLORS[type],
                                  fontWeight: 700, fontSize: '0.75rem', mr: 1,
                                }}
                              />
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{desc}</Typography>
                          </Box>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 180 }}>
                            <ScheduleIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                            <Typography variant="caption" color="text.secondary" noWrap>Cooldown:</Typography>
                            <TextField
                              size="small" type="number"
                              value={cooldown}
                              onChange={e => updateCooldown(type, parseInt(e.target.value) || 0)}
                              disabled={!enabled}
                              sx={{ width: 70 }}
                              inputProps={{ min: 1, max: 336 }}
                            />
                            <Typography variant="caption" color="text.secondary">hrs</Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </Box>

              <Divider />

              {/* Schedule cron */}
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1}>
                  Timer Schedule
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Azure Functions NCRONTAB expression. The current schedule runs every 3 hours.
                  Changing this here saves the config but you'll need to redeploy the Azure Function to change the actual cron.
                </Typography>
                <TextField
                  size="small"
                  value={config.schedule_cron}
                  onChange={e => updateConfigField('schedule_cron', e.target.value)}
                  sx={{ width: 260 }}
                  helperText="e.g. 0 0 */3 * * * = every 3 hours"
                />
              </Box>

              {/* Save */}
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  variant="contained"
                  onClick={handleSaveConfig}
                  disabled={!configDirty || savingConfig}
                  startIcon={savingConfig ? <CircularProgress size={16} color="inherit" /> : <SettingsIcon />}
                  sx={{
                    borderRadius: '20px', textTransform: 'none', fontWeight: 700, px: 3,
                    bgcolor: '#9C27B0', '&:hover': { bgcolor: '#7B1FA2' },
                  }}
                >
                  {savingConfig ? 'Saving...' : 'Save Configuration'}
                </Button>
                {configDirty && (
                  <Button
                    variant="text" size="small"
                    onClick={() => { fetchConfig(); setConfigDirty(false); }}
                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                  >
                    Reset to saved
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>
        ) : (
          <Alert severity="info" sx={{ mb: 3 }}>
            No scheduler configuration found. Default values are being used. Open this section and save to persist custom settings.
          </Alert>
        )}
      </Collapse>
    </Box>
  );
}

// ──────────────────────────────────────────
// Shared sub-components
// ──────────────────────────────────────────

function SectionHeader({ icon, title, open, onToggle, badge }: {
  icon: React.ReactNode;
  title: string;
  open: boolean;
  onToggle: () => void;
  badge?: number;
}) {
  return (
    <Stack
      direction="row" alignItems="center" spacing={1} mb={open ? 1.5 : 2.5} mt={1}
      onClick={onToggle}
      sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.8 } }}
    >
      {icon}
      <Typography variant="h6" fontWeight={700}>{title}</Typography>
      {badge != null && badge > 0 && (
        <Chip label={badge} size="small" sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 700, fontSize: '0.75rem', height: 22 }} />
      )}
      <Box flex={1} />
      <IconButton size="small">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
    </Stack>
  );
}

function StatCard({ label, value, color, small }: { label: string; value: number; color: string; small?: boolean }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: small ? 1.5 : 2,
        borderRadius: 2,
        minWidth: small ? 120 : 150,
        textAlign: 'center',
        borderColor: `${color}40`,
      }}
    >
      <Typography variant={small ? 'h6' : 'h4'} fontWeight={800} sx={{ color }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
    </Paper>
  );
}
